import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import ConsumptionForm from '@components/common/forms/ConsumptionForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const ResourceConsumePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchResource = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/resources/${id}`);
                setResource(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch resource:', err);
                setError(t('resourceConsume.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            const response = await api.consumptions.create({
                resource_id: id,
                quantity_consumed: formData.quantity_consumed,
                notes: formData.notes || null
            });
            notify.success(t('consumption.consumed_successfully'));
            navigate(`/consumptions/${response.data.id}`);
        } catch (err) {
            console.error('Failed to consume resource:', err);
            notify.error(t('resourceConsume.consumeError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/resources/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('resources.resourceConsume.consumeResource')}
                subtitle={t('resourceConsume.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !resource) {
        return (
            <FormPageLayout
                title={t('resources.resourceConsume.consumeResource')}
                subtitle={error || t('resourceConsume.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('resources.resourceConsume.consumeResource')}
            subtitle={t('resources.resourceConsume.subtitle', { resourceName: resource.name })}
            onBack={handleCancel}
        >
            <ConsumptionForm
                resource={resource}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </FormPageLayout>
    );
};

export default ResourceConsumePage;
